import { createContext, useEffect, useReducer } from 'react';

/**
 * @typedef {Object} User
 * @property {string} id User ID
 * @property {string} email User email
 * @property {boolean} isAdmin Whether the user is an admin
 */

/**
 * @typedef {Object} AuthState
 * @property {?User} user The authenticated user object
 * @property {?string} token The authentication token
 */

/**
 * @typedef {Object} AuthContextType
 * @property {?User} user The authenticated user object
 * @property {?string} token The authentication token
 * @property {import('react').Dispatch<AuthAction>} dispatch The dispatch function for auth actions
 */

/**
 * @typedef {Object} AuthAction
 * @property {'login' | 'logout'} type The type of auth action
 * @property {Object} [payload] The action payload
 * @property {User} [payload.user] The user object
 * @property {string} [payload.token] The auth token
 */

/** @type {import('react').Context<AuthContextType>} */
export const AuthContext = createContext();

/**
 * Reducer function for auth state management
 * @param {AuthState} state - The current auth state
 * @param {AuthAction} action - The action to process
 * @returns {AuthState} The new auth state
 */
export function authReducer(state, action) {
  switch (action.type) {
    case 'login':
      return {
        user: action.payload?.user ?? null,
        token: action.payload?.token ?? null
      };
    case 'logout':
      return {
        user: null,
        token: null
      };
    default:
      return state;
  }
}

/**
 * Auth context provider component
 * @param {import('react').PropsWithChildren} props The component props
 * @returns {import('react').JSX.Element}
 */
export function AuthContextProvider(props) {
  const { children } = props;

  /** @type {[AuthState, import('react').Dispatch<AuthAction>]} */
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    token: null
  });

  useEffect(function () {
    /** @type {?User} */
    const user = JSON.parse(localStorage.getItem('user'));

    /** @type {?string} */
    const token = localStorage.getItem('token');

    if (user && token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expirationTime = payload.exp * 1000;

        if (expirationTime < Date.now()) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');

          return dispatch({
            type: 'logout'
          });
        }

        dispatch({
          type: 'login',
          payload: { user, token }
        });
      } catch (error) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');

        dispatch({
          type: 'logout'
        });
      }
    }
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, dispatch }}>
      {children}
    </AuthContext.Provider>
  );
}
